import {
  afterNextRender,
  Component,
  effect,
  ChangeDetectorRef,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import {
  clearAddressSection,
  createShipmentAddressesForm,
  expandAddressLines,
  expandAddressLinesWhenSpecialStreetRequiresCivic,
  refreshAddressSectionWithRetrieveFields,
} from './forms/shipment-addresses.form';
import type {
  ShipmentAddressSectionKey,
  ShipmentAddressesFormValue,
} from './models/address-form.model';
import type { AddressAutocompleteSelection } from '../../core/models/address-autocomplete';
import type { AddressRetrieveFormFields } from '../../core/models/address-retrieve';
import type { ValidateAddressResponse } from '../../core/models/address-validate';
import { AddressValidationApiService } from '../../core/api/address-validation-api.service';
import { ServiceAbilityApiService } from '../../core/api/service-ability-api.service';
import { AnalyticsService } from '../../core/integrations/analytics';
import { ShipmentFlowStateService } from '../../core/services/shipment-flow-state.service';
import { readPageSkeletonPreviewQueryFlag } from '../../shared/utils/e2e-query-flags';
import { DelayedLoadingController } from '../../shared/utils/delayed-loading';
import { redirectIfCompletedShipmentActive } from '../get-labels/utils/redirect-if-completed-shipment-active';
import { AddressFormSection } from './sections/address-form-section/address-form-section';
import { ReturnAddressSection } from './sections/return-address-section/return-address-section';
import { ShipFromShipToSkeleton } from './sections/ship-from-ship-to-skeleton/ship-from-ship-to-skeleton';
import { ShipmentFormActions } from './sections/shipment-form-actions/shipment-form-actions';
import { ShipmentStepper } from './sections/shipment-stepper/shipment-stepper';
import {
  validateShipmentAddressesForm,
  collectServiceabilityErrorSummary,
} from './utils/address-form-errors';
import { focusFirstValidationError } from './utils/focus-first-validation-error';
import { mapShipmentAddressesToValidateRequest } from './utils/map-shipment-addresses-to-validate-request';
import { mapAddressValidationTokenToServiceAbilityRequest } from './utils/map-shipment-addresses-to-service-ability-request';
import {
  applyAddressServiceabilityErrors,
  clearAddressServiceabilityErrors,
} from './utils/apply-address-serviceability-errors';
import {
  applyAddressNotFoundErrors,
  clearAddressNotFoundErrors,
} from './utils/apply-address-not-found-errors';
import { mapValidateResultsToAddressErrors } from './utils/map-validate-results-to-address-errors';
import { resolveSystemErrorKey } from './utils/resolve-system-error-key';
import {
  mapSuggestedAddressToAddressSectionFields,
  mapSuggestedAddressToReturnAddressFields,
} from './utils/map-suggested-address-to-form-fields';
import { mapAddressRetrieveDetailToFormFields } from './utils/map-address-retrieve-to-form-fields';
import { selectAddressRetrieveDetailForLocale } from './utils/select-address-retrieve-detail';
import {
  mapValidateResultsToSuggestions,
  type AddressSuggestionPresentation,
} from './utils/map-validate-results-to-suggestions';
import {
  buildAddressValidationAcceptanceSnapshot,
  hasActiveAddressSuggestions,
  isAddressValidationAccepted,
  type AddressValidationAcceptanceSnapshot,
} from './utils/address-validation-acceptance';
import { TranslationService } from '../../core/i18n';
import { ValidationMessageService } from './services/validation-message.service';
import { SystemErrorBanner } from './sections/system-error-banner/system-error-banner';
import { ServiceabilityErrorBanner } from './sections/serviceability-error-banner/serviceability-error-banner';

/**
 * Ship From / Ship To address step composed from reusable section components.
 */
@Component({
  selector: 'app-ship-from-ship-to',
  imports: [
    ReactiveFormsModule,
    ShipmentStepper,
    AddressFormSection,
    ReturnAddressSection,
    ShipmentFormActions,
    SystemErrorBanner,
    ServiceabilityErrorBanner,
    ShipFromShipToSkeleton,
  ],
  providers: [ValidationMessageService],
  templateUrl: './ship-from-ship-to.html',
  styleUrl: './ship-from-ship-to.scss',
})
export class ShipFromShipTo implements OnInit {
  private readonly formBuilder = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly addressValidationApi = inject(AddressValidationApiService);
  private readonly serviceAbilityApi = inject(ServiceAbilityApiService);
  private readonly title = inject(Title);
  private readonly translation = inject(TranslationService);
  private readonly validationMessages = inject(ValidationMessageService);
  private readonly router = inject(Router);
  private readonly shipmentFlowState = inject(ShipmentFlowStateService);
  private readonly analytics = inject(AnalyticsService);

  readonly form = createShipmentAddressesForm(this.formBuilder);

  /**
   * True when the delayed page skeleton should paint (OM-711: after 1s of initial pending).
   */
  protected readonly showPageSkeleton = signal(false);
  /** True while AVS and serviceability run after Save and Continue. */
  protected readonly saveAndContinueInProgress = signal(false);
  /**
   * Blocking enter-time load for the page skeleton. Remains false today because Ship From/To
   * paints from client state; Addresses/validate runs on Save (not initial display).
   * `?pageSkeletonPreview=1` keeps pending true for E2E/QA skeleton a11y scans only.
   */
  private readonly initialContentPending = signal(readPageSkeletonPreviewQueryFlag());
  private readonly delayedInitialLoading = new DelayedLoadingController((show) => {
    this.showPageSkeleton.set(show);
  });

  readonly shipFromSectionKey = signal(0);
  readonly shipToSectionKey = signal(0);
  readonly returnAddressSectionKey = signal(0);
  protected readonly shipFromSubmitted = signal(false);
  protected readonly shipToSubmitted = signal(false);
  protected readonly returnAddressSubmitted = signal(false);
  protected readonly validationAttempt = signal(0);
  protected readonly shipFromSuggestion = signal<AddressSuggestionPresentation | null>(null);
  protected readonly shipToSuggestion = signal<AddressSuggestionPresentation | null>(null);
  protected readonly returnAddressSuggestion = signal<AddressSuggestionPresentation | null>(null);
  /** Non-blocking missing unit/suite warning for Ship To after AVS. */
  protected readonly shipToMissingUnitSuiteWarning = signal(false);
  /**
   * Sections with unresolved AVS address-not-found errors.
   * Tracked separately because Angular FormGroup.setErrors is cleared by tree revalidation
   * when a sibling section is remounted (e.g. Use Suggested Address).
   */
  private readonly addressNotFoundSections = signal<readonly ShipmentAddressSectionKey[]>([]);
  /** Set when address validation is accepted and the flow may advance to the package step. */
  readonly readyForPackageStep = signal(false);
  protected readonly activeStepIndex = 0;

  private acceptedValidationSnapshot: AddressValidationAcceptanceSnapshot | null = null;
  private unitSuiteWarningAcknowledgedSnapshot: string | null = null;

  constructor() {
    effect(() => {
      this.translation.revision();
      const pageTitle = this.translation.translate('createShipment.pageTitle');
      const appTitle = this.translation.translate('app.title');
      this.title.setTitle(`${pageTitle} | ${appTitle}`);
    });

    effect(() => {
      this.delayedInitialLoading.setPending(this.initialContentPending());
    });
    this.destroyRef.onDestroy(() => this.delayedInitialLoading.destroy());

    this.bindShipToMissingUnitSuiteWatchers();
  }

  /**
   * When a completed shipment is active, returns the customer to Get Labels (OM-66 AC7)
   * before restoring wizard state so earlier steps cannot be re-entered.
   */
  ngOnInit(): void {
    if (redirectIfCompletedShipmentActive(this.shipmentFlowState, this.router)) {
      return;
    }

    const navigationState = this.router.currentNavigation()?.extras.state as
      Record<string, unknown> | undefined;
    const historyState =
      typeof window !== 'undefined'
        ? (window.history.state as Record<string, unknown> | null)
        : null;
    const systemErrorKey =
      navigationState?.['showSystemError'] ?? historyState?.['showSystemError'];
    if (systemErrorKey === 'createShipment.system.unexpectedError') {
      this.validationMessages.showSystemError(systemErrorKey);
      this.clearShowSystemErrorFromHistory();
    }

    this.restorePersistedState();
  }

  /**
   * Removes a one-shot showSystemError flag from history.state so reload / back-nav
   * does not re-display the banner after it has already been consumed.
   */
  private clearShowSystemErrorFromHistory(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const current = window.history.state;
    if (current === null || typeof current !== 'object' || Array.isArray(current)) {
      return;
    }

    if (!('showSystemError' in current)) {
      return;
    }

    const rest = { ...(current as Record<string, unknown>) };
    delete rest['showSystemError'];
    window.history.replaceState(rest, '');
  }

  /** Ship From nested address form group. */
  get shipFromForm(): FormGroup {
    return this.form.controls['shipFrom'] as FormGroup;
  }

  /** Ship To nested address form group. */
  get shipToForm(): FormGroup {
    return this.form.controls['shipTo'] as FormGroup;
  }

  /** Return address nested form group. */
  get returnAddressForm(): FormGroup {
    return this.form.controls['returnAddress'] as FormGroup;
  }

  /**
   * Restores address step values from session storage when navigating back from a later step.
   */
  private restorePersistedState(): void {
    const persisted = this.shipmentFlowState.load();

    if (!persisted?.addresses) {
      return;
    }

    this.form.patchValue(persisted.addresses);
    this.cdr.markForCheck();
  }

  /**
   * Expands optional address lines for the requested section.
   */
  onExpandAddressLines(section: ShipmentAddressSectionKey): void {
    expandAddressLines(this.form, section);
    this.cdr.markForCheck();
  }

  /**
   * Clears the requested address section back to empty defaults.
   * When addresses were already saved to flow state, syncs the cleared form so a refresh
   * does not restore stale values, and drops AVS/SA attestations.
   */
  onClearSection(section: ShipmentAddressSectionKey): void {
    clearAddressSection(this.form, section, this.formBuilder);
    this.clearSuggestionForSection(section);
    this.clearValidationAcceptance();
    this.syncClearedAddressesToFlowState();

    if (section === 'shipFrom') {
      this.shipFromSectionKey.update((key) => key + 1);
      this.shipFromSubmitted.set(false);
      this.scheduleClearButtonFocus(section);
      return;
    }

    if (section === 'shipTo') {
      this.clearMissingUnitSuiteWarning();
      this.shipToSectionKey.update((key) => key + 1);
      this.shipToSubmitted.set(false);
      this.scheduleClearButtonFocus(section);
      return;
    }

    this.returnAddressSectionKey.update((key) => key + 1);
    this.returnAddressSubmitted.set(false);
    this.scheduleClearButtonFocus(section);
  }

  /**
   * Writes the current address form into session storage after Clear Form when a prior
   * address snapshot exists. No-ops before the first Save and Continue.
   */
  private syncClearedAddressesToFlowState(): void {
    if (!this.shipmentFlowState.load()?.addresses) {
      return;
    }

    this.shipmentFlowState.clearAttestations();
    this.shipmentFlowState.saveAddressStep(this.form.getRawValue() as ShipmentAddressesFormValue);
  }

  /**
   * Returns focus to the section Clear Form control after the section remounts.
   */
  private scheduleClearButtonFocus(section: ShipmentAddressSectionKey): void {
    afterNextRender(
      () => {
        const clearHost = this.hostElement.nativeElement.querySelector(
          `[data-clear-section="${section}"]`,
        );
        const focusTarget = clearHost?.querySelector('button') ?? clearHost ?? null;
        (focusTarget as HTMLElement | null)?.focus();
      },
      { injector: this.injector },
    );
  }

  /**
   * Returns focus to Suite/Apt./Unit after autocomplete retrieve remounts the section,
   * preserving keyboard tab order (street → suite → …).
   */
  private schedulePostRetrieveSuiteFocus(section: ShipmentAddressSectionKey): void {
    afterNextRender(
      () => {
        const suiteInput = this.hostElement.nativeElement.querySelector(
          `input[name="${section}-suiteAptUnit"]`,
        );
        (suiteInput as HTMLElement | null)?.focus();
      },
      { injector: this.injector },
    );
  }

  /**
   * Calls GET /addresses/retrieve for a selected autocomplete suggestion and populates the section.
   */
  onStreetAddressSuggestionSelected(
    section: ShipmentAddressSectionKey,
    selection: AddressAutocompleteSelection,
  ): void {
    void this.populateSectionFromAutocompleteSelection(section, selection);
  }

  /**
   * Merges retrieve fields into a section and recreates its UI so kit inputs display new values.
   */
  onAddressRetrieveApplied(
    section: ShipmentAddressSectionKey,
    fields: AddressRetrieveFormFields,
  ): void {
    refreshAddressSectionWithRetrieveFields(this.form, section, fields, this.formBuilder);
    this.clearSuggestionForSection(section);
    this.clearValidationAcceptance();

    if (section === 'shipFrom') {
      this.shipFromSectionKey.update((key) => key + 1);
    } else if (section === 'shipTo') {
      this.shipToSectionKey.update((key) => key + 1);
    } else {
      this.returnAddressSectionKey.update((key) => key + 1);
    }

    this.schedulePostRetrieveSuiteFocus(section);
    this.cdr.markForCheck();
  }

  /**
   * Loads structured address fields from Canada Post retrieve and applies them to the section.
   */
  private async populateSectionFromAutocompleteSelection(
    section: ShipmentAddressSectionKey,
    selection: AddressAutocompleteSelection,
  ): Promise<void> {
    try {
      const details = await this.addressValidationApi.retrieve(selection.id);
      const detail = selectAddressRetrieveDetailForLocale(details, this.translation.locale());

      if (!detail) {
        return;
      }

      const fields = mapAddressRetrieveDetailToFormFields(detail);
      this.onAddressRetrieveApplied(section, fields);
    } catch {
      // Leave the user's typed street value when retrieve fails.
    } finally {
      this.cdr.markForCheck();
    }
  }

  /**
   * Updates the return-address toggle on the parent form.
   */
  onUseDifferentReturnAddressChange(checked: boolean): void {
    this.form.controls['useDifferentReturnAddress'].setValue(checked);
    this.clearValidationAcceptance();

    if (!checked) {
      this.returnAddressSubmitted.set(false);
      this.returnAddressSuggestion.set(null);
    }

    this.cdr.markForCheck();
  }

  /**
   * Applies AVS suggested address fields to the requested section.
   */
  onUseSuggestedAddress(section: ShipmentAddressSectionKey): void {
    const suggestion = this.getSuggestionForSection(section);
    if (!suggestion) {
      return;
    }

    const sectionGroup = this.form.get(section) as FormGroup;
    const currentValue = sectionGroup.getRawValue();

    const fields =
      section === 'returnAddress'
        ? mapSuggestedAddressToReturnAddressFields(suggestion.suggestedAddress, {
            suiteAptUnit: currentValue.suiteAptUnit ?? '',
            residential: currentValue.residential === true,
          })
        : mapSuggestedAddressToAddressSectionFields(suggestion.suggestedAddress, {
            companyOrFullName: currentValue.companyOrFullName ?? '',
            suiteAptUnit: currentValue.suiteAptUnit ?? '',
            residential: currentValue.residential === true,
          });

    refreshAddressSectionWithRetrieveFields(this.form, section, fields, this.formBuilder);
    // Remounting a section via setControl revalidates the form tree and wipes sibling
    // addressNotFound FormGroup errors — restore them from tracked sections (OM-452).
    this.reapplyTrackedAddressNotFoundErrors();
    this.onSuggestionResolved(section);

    if (section === 'shipFrom') {
      this.shipFromSectionKey.update((key) => key + 1);
    } else if (section === 'shipTo') {
      this.shipToSectionKey.update((key) => key + 1);
    } else {
      this.returnAddressSectionKey.update((key) => key + 1);
    }

    this.cdr.markForCheck();
  }

  /**
   * Dismisses the suggested address panel while keeping the user's original entry.
   */
  onKeepSuggestedAddressEntry(section: ShipmentAddressSectionKey): void {
    this.onSuggestionResolved(section);
    this.cdr.markForCheck();
  }

  /**
   * Validates address sections, runs AVS, then serviceability when AVS is clean or resolved.
   * Increments validationAttempt after client-side validation so section summaries include
   * supplemental rural/PO Box errors on the first Save (OM-97).
   */
  onSaveAndContinue(): void {
    this.shipFromSubmitted.set(true);
    this.shipToSubmitted.set(true);

    if (this.form.controls['useDifferentReturnAddress'].value === true) {
      this.returnAddressSubmitted.set(true);
    }

    clearAddressServiceabilityErrors(this.form);
    clearAddressNotFoundErrors(this.form);
    this.validationMessages.clearServiceabilityErrors();

    expandAddressLinesWhenSpecialStreetRequiresCivic(this.form);

    const addressesValid = validateShipmentAddressesForm(this.form);
    this.validationAttempt.update((attempt) => attempt + 1);
    this.cdr.detectChanges();

    if (!addressesValid) {
      queueMicrotask(() => focusFirstValidationError(this.hostElement.nativeElement));
      return;
    }

    if (this.hasActiveSuggestions()) {
      return;
    }

    if (isAddressValidationAccepted(this.form.getRawValue(), this.acceptedValidationSnapshot)) {
      this.proceedToPackageStep();
      return;
    }

    void this.runValidationFlow();
  }

  /**
   * Runs AVS first. Canada Post outage with a fallback JWT skips suggestions and still
   * calls serviceability. Address Validation Lambda failure blocks continue.
   */
  private async runValidationFlow(): Promise<void> {
    if (this.saveAndContinueInProgress()) {
      return;
    }

    this.saveAndContinueInProgress.set(true);
    this.cdr.markForCheck();

    try {
      this.clearValidationAcceptance();
      this.shipmentFlowState.clearAttestations();

      let addressValidationToken!: string;

      try {
        const avsResponse = await this.addressValidationApi.validate(
          mapShipmentAddressesToValidateRequest(this.form.getRawValue()),
        );
        if (!avsResponse.attestation) {
          this.clearAllSuggestions();
          this.validationMessages.showSystemError('createShipment.system.unexpectedError');
          this.cdr.markForCheck();
          return;
        }

        addressValidationToken = avsResponse.attestation;
        this.shipmentFlowState.saveAttestation('addressValidation', avsResponse.attestation);

        if (avsResponse.validationAvailable === false) {
          this.clearAllSuggestions();
          this.clearAddressNotFoundTracking();
          await this.runServiceabilityCheckAndProceed(addressValidationToken);
          return;
        }

        this.applyAddressValidationResponse(avsResponse);

        if (this.hasAddressNotFoundErrors()) {
          return;
        }

        if (this.shouldPauseForMissingUnitSuiteWarning()) {
          this.cdr.markForCheck();
          return;
        }

        if (this.hasActiveSuggestions()) {
          return;
        }
      } catch (error) {
        this.clearAllSuggestions();
        this.clearAddressNotFoundTracking();
        this.validationMessages.showSystemError(resolveSystemErrorKey(error));
        this.cdr.markForCheck();
        return;
      }

      await this.runServiceabilityCheckAndProceed(addressValidationToken);
    } finally {
      this.saveAndContinueInProgress.set(false);
      this.cdr.markForCheck();
    }
  }

  /**
   * Calls serviceability with the AVS attestation. Navigates when the route is serviceable.
   */
  private async runServiceabilityCheckAndProceed(addressValidationToken: string): Promise<void> {
    clearAddressServiceabilityErrors(this.form);
    this.validationMessages.clearServiceabilityErrors();

    try {
      const serviceResponse = await this.serviceAbilityApi.check(
        mapAddressValidationTokenToServiceAbilityRequest(addressValidationToken),
      );

      if (!serviceResponse.serviceable) {
        if (isAddressValidationTokenRequired(serviceResponse)) {
          this.clearAllSuggestions();
          this.validationMessages.showSystemError('createShipment.system.validationUnavailable');
          this.cdr.markForCheck();
          return;
        }

        // OM-462: SA upstream/system failure returns serviceable:false with a distinct
        // messageKey — must not be shown as "address is not serviceable".
        if (isServiceAbilityUpstreamUnavailable(serviceResponse)) {
          this.clearAllSuggestions();
          this.validationMessages.showSystemError(
            'createShipment.system.requestTemporarilyUnavailable',
          );
          this.cdr.markForCheck();
          return;
        }

        this.applyServiceabilityFailure(serviceResponse.message);
        return;
      }

      const serviceAbilityAttestation = serviceResponse.attestation?.trim();

      if (!serviceAbilityAttestation) {
        this.clearAllSuggestions();
        this.validationMessages.showSystemError('createShipment.system.unexpectedError');
        this.cdr.markForCheck();
        return;
      }

      this.shipmentFlowState.saveAttestation('serviceAbility', serviceAbilityAttestation);

      if (typeof serviceResponse.destination?.residential === 'boolean') {
        this.shipmentFlowState.saveDestinationResidential(serviceResponse.destination.residential);
      }
    } catch (error) {
      this.clearAllSuggestions();
      this.validationMessages.showSystemError(resolveSystemErrorKey(error));
      this.cdr.markForCheck();
      return;
    }

    this.markValidationAccepted();
    this.validationMessages.clearSystemError();
    this.proceedToPackageStep();
    this.cdr.markForCheck();
  }

  /**
   * Surfaces unserviceable address errors at the top of the page after submit.
   */
  private applyServiceabilityFailure(message: string): void {
    applyAddressServiceabilityErrors(this.form, message);
    this.validationMessages.showServiceabilityErrors(
      collectServiceabilityErrorSummary(this.form, (key, params) =>
        this.translation.translate(key, params),
      ),
    );
    this.shipFromSubmitted.set(true);
    this.shipToSubmitted.set(true);

    if (this.form.controls['useDifferentReturnAddress'].value === true) {
      this.returnAddressSubmitted.set(true);
    }

    this.validationAttempt.update((attempt) => attempt + 1);
    this.cdr.detectChanges();
    queueMicrotask(() => focusFirstValidationError(this.hostElement.nativeElement));
  }

  /**
   * Persists address data and navigates to the package step after validation is accepted.
   */
  private proceedToPackageStep(): void {
    const snapshot = this.form.getRawValue() as ShipmentAddressesFormValue;
    this.analytics.trackEvent({
      event: 'email notification',
      senderReceiveUpdates: snapshot.shipFrom.receiveNotifications === true,
      recipientReceiveUpdates: snapshot.shipTo.receiveNotifications === true,
    });
    this.shipmentFlowState.saveAddressStep(snapshot);
    this.readyForPackageStep.set(true);
    void this.router.navigate(['/package']);
  }

  /**
   * Clears cached validation acceptance so a form change triggers a fresh AVS + serviceability call.
   */
  private clearValidationAcceptance(): void {
    clearAddressServiceabilityErrors(this.form);
    this.validationMessages.clearServiceabilityErrors();
    this.clearAddressNotFoundTracking();
    this.validationMessages.clearSystemError();
    this.acceptedValidationSnapshot = null;
    this.readyForPackageStep.set(false);
  }

  /**
   * Stores the current form as accepted after suggestions are fully resolved.
   */
  private markValidationAccepted(): void {
    this.acceptedValidationSnapshot = buildAddressValidationAcceptanceSnapshot(
      this.form.getRawValue(),
    );
  }

  /**
   * Clears one suggestion panel. When AVS is fully resolved, runs serviceability before proceeding.
   * Reuses the AVS attestation already persisted on flow state (no re-validate).
   */
  private onSuggestionResolved(section: ShipmentAddressSectionKey): void {
    this.clearSuggestionForSection(section);

    if (this.hasActiveSuggestions() || this.hasAddressNotFoundErrors()) {
      return;
    }

    const addressValidationToken = this.shipmentFlowState.load()?.attestations?.addressValidation;
    if (!addressValidationToken) {
      this.clearAllSuggestions();
      this.validationMessages.showSystemError('createShipment.system.unexpectedError');
      this.cdr.markForCheck();
      return;
    }

    void this.runServiceabilityCheckAndProceed(addressValidationToken);
  }

  /**
   * Returns true when any section still shows an unresolved suggested-address panel.
   */
  private hasActiveSuggestions(): boolean {
    return hasActiveAddressSuggestions(
      this.shipFromSuggestion(),
      this.shipToSuggestion(),
      this.returnAddressSuggestion(),
    );
  }

  /**
   * Returns true when any section still has an unresolved AVS address-not-found error.
   */
  private hasAddressNotFoundErrors(): boolean {
    return this.addressNotFoundSections().length > 0;
  }

  /**
   * Clears tracked and form-level address-not-found errors.
   */
  private clearAddressNotFoundTracking(): void {
    this.addressNotFoundSections.set([]);
    clearAddressNotFoundErrors(this.form);
  }

  /**
   * Re-applies form-level addressNotFound errors from the tracked section list.
   * Needed after setControl remounts wipe FormGroup.setErrors via tree revalidation.
   */
  private reapplyTrackedAddressNotFoundErrors(): void {
    const sections = this.addressNotFoundSections();
    if (sections.length === 0) {
      return;
    }

    applyAddressNotFoundErrors(this.form, [...sections]);
  }

  /**
   * Watches Ship To suite and address fields so the warning clears or re-evaluates.
   */
  private bindShipToMissingUnitSuiteWatchers(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (!this.shipToSuiteIsBlank()) {
        this.shipToMissingUnitSuiteWarning.set(false);
      }

      const snapshot = this.buildShipToWarningSnapshot();
      if (
        this.unitSuiteWarningAcknowledgedSnapshot &&
        this.unitSuiteWarningAcknowledgedSnapshot !== snapshot
      ) {
        this.unitSuiteWarningAcknowledgedSnapshot = null;
      }
    });
  }

  /**
   * Sets the Ship To missing unit/suite warning from the AVS recipient result.
   */
  private applyMissingUnitSuiteWarningFromResponse(response: ValidateAddressResponse): void {
    const recipient = response.addressValidationResults.find(
      (result) => result.userType === 'recipient',
    );
    this.shipToMissingUnitSuiteWarning.set(
      Boolean(recipient?.missingUnitSuiteWarning) && this.shipToSuiteIsBlank(),
    );
  }

  /**
   * Pauses navigation once so the customer can add a suite or continue without one.
   */
  private shouldPauseForMissingUnitSuiteWarning(): boolean {
    if (!this.shipToMissingUnitSuiteWarning() || !this.shipToSuiteIsBlank()) {
      return false;
    }

    const snapshot = this.buildShipToWarningSnapshot();
    if (this.unitSuiteWarningAcknowledgedSnapshot === snapshot) {
      return false;
    }

    this.unitSuiteWarningAcknowledgedSnapshot = snapshot;
    return true;
  }

  /**
   * Clears the missing unit/suite warning and acknowledgement (Ship To clear or reset).
   */
  private clearMissingUnitSuiteWarning(): void {
    this.shipToMissingUnitSuiteWarning.set(false);
    this.unitSuiteWarningAcknowledgedSnapshot = null;
  }

  /**
   * True when the Ship To Suite/Apt./Unit field is empty.
   */
  private shipToSuiteIsBlank(): boolean {
    const suite = this.form.controls['shipTo'].get('suiteAptUnit')?.value;
    return typeof suite !== 'string' || suite.trim().length === 0;
  }

  /**
   * Snapshot of Ship To fields used to decide whether a second Save & Continue is the same address.
   */
  private buildShipToWarningSnapshot(): string {
    const shipTo = this.form.controls['shipTo'].getRawValue();

    return JSON.stringify({
      streetAddress: shipTo.streetAddress,
      suiteAptUnit: shipTo.suiteAptUnit,
      addressLine2: shipTo.addressLine2,
      addressLine3: shipTo.addressLine3,
      city: shipTo.city,
      province: shipTo.province,
      postalCode: shipTo.postalCode,
    });
  }

  /**
   * Applies AVS suggestions and not-found errors to their respective sections.
   */
  private applyAddressValidationResponse(response: ValidateAddressResponse): void {
    this.clearAddressNotFoundTracking();

    const suggestions = mapValidateResultsToSuggestions(response, this.form.getRawValue());
    this.shipFromSuggestion.set(suggestions.shipFrom ?? null);
    this.shipToSuggestion.set(suggestions.shipTo ?? null);
    this.returnAddressSuggestion.set(suggestions.returnAddress ?? null);

    const notFoundSections = mapValidateResultsToAddressErrors(response);
    this.addressNotFoundSections.set(notFoundSections);

    if (notFoundSections.length > 0) {
      applyAddressNotFoundErrors(this.form, notFoundSections);
      this.shipFromSubmitted.set(true);
      this.shipToSubmitted.set(true);

      if (this.form.controls['useDifferentReturnAddress'].value === true) {
        this.returnAddressSubmitted.set(true);
      }

      this.shipToMissingUnitSuiteWarning.set(false);
      this.validationAttempt.update((attempt) => attempt + 1);
      this.cdr.detectChanges();
      queueMicrotask(() => focusFirstValidationError(this.hostElement.nativeElement));
      return;
    }

    this.applyMissingUnitSuiteWarningFromResponse(response);
  }

  /**
   * Clears all active suggested-address panels.
   */
  private clearAllSuggestions(): void {
    this.shipFromSuggestion.set(null);
    this.shipToSuggestion.set(null);
    this.returnAddressSuggestion.set(null);
  }

  /**
   * Clears the suggested-address panel for one section.
   */
  private clearSuggestionForSection(section: ShipmentAddressSectionKey): void {
    if (section === 'shipFrom') {
      this.shipFromSuggestion.set(null);
      return;
    }

    if (section === 'shipTo') {
      this.shipToSuggestion.set(null);
      return;
    }

    this.returnAddressSuggestion.set(null);
  }

  /**
   * Returns the active suggestion state for a section, if any.
   */
  private getSuggestionForSection(
    section: ShipmentAddressSectionKey,
  ): AddressSuggestionPresentation | null {
    if (section === 'shipFrom') {
      return this.shipFromSuggestion();
    }

    if (section === 'shipTo') {
      return this.shipToSuggestion();
    }

    return this.returnAddressSuggestion();
  }
}

/**
 * True when Service Ability rejected the request because JWT_avs is missing or invalid.
 * That is a validation outage, not a genuine unserviceable address.
 */
function isAddressValidationTokenRequired(response: {
  message?: string;
  messageKey?: string;
}): boolean {
  if (response.messageKey === 'serviceAbility.errors.addressValidationTokenRequired') {
    return true;
  }

  return /addressValidationToken is required/i.test(response.message ?? '');
}

/**
 * True when service-ability could not complete because ESD/upstream was unavailable (OM-462).
 * Backend returns HTTP 500 with serviceable:false and this messageKey; the UI must show a
 * temporary system error, not the unserviceable-address copy.
 */
function isServiceAbilityUpstreamUnavailable(response: {
  message?: string;
  messageKey?: string;
}): boolean {
  if (response.messageKey === 'serviceAbility.errors.upstreamUnavailable') {
    return true;
  }

  return /service ability check is temporarily unavailable/i.test(response.message ?? '');
}
